import { observable } from 'mobx'
import {
  AetTodistusKieli,
  editIlmoitettuArvo,
  editVtjArvo,
  KirkollinenVihkiminen,
  Puhelinnumero,
  Sahkopostiosoite,
  SukulaisuussuhdeVakuutettu,
  Etunimet,
  Sukunimi,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  AetOrderer,
  AetOrdererUiForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { TodistusAsiointikieli } from 'todistukset-common/src/vtj/types/todistus-person.type'
import type { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { isHetuIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

export enum AetLastnameSelection {
  CURRENT_NAME = 'NYKYINEN',
  NEW_NAME = 'UUSI',
}

export const createAetOrdererFormStore = (
  user: TodistuksetUser | undefined,
  asiointikieli: TodistusAsiointikieli
): AetOrdererUiForm => {
  return observable({
    orderer: getAetOrdererFromUser(user, asiointikieli),
    keepingCurrentLastName: null,
    newLastName: null,
    sukulaisuussuhdeVakuutettu: editIlmoitettuArvo<SukulaisuussuhdeVakuutettu>(
      null,
      false
    ),
    spouseHetu: '',
    spouseEmail: '',
    spouseKeepingCurrentLastName: null,
    spouseNewLastName: null,
    placeOfMarriage: null,
    isKirkollinenVihkiminen: editIlmoitettuArvo<KirkollinenVihkiminen>(
      null,
      false
    ),
    todistusLanguage: editIlmoitettuArvo<AetTodistusKieli>(null, null),
    contactInfo: {
      email: editIlmoitettuArvo<Sahkopostiosoite>(null, ''),
      phoneNumber: editIlmoitettuArvo<Puhelinnumero>(null, ''),
    },
  })
}

export const useAetOrdererForm = (): AetOrdererUiForm => {
  const { aetOrdererForm } = useStore()
  return aetOrdererForm
}

export const getAetOrdererFromUser = (
  user: TodistuksetUser | undefined,
  asiointikieli: TodistusAsiointikieli
): AetOrderer => {
  const getHetuIdentifiedFields = (user: TodistuksetUser | undefined) => {
    if (!user || !isHetuIdentifiedUser(user)) {
      return {
        hetu: '',
        numberOfMarriages: null,
        maritalStatus: null,
        countryOfResidenceCode: null,
        citizenshipCode: null,
        religiousCommunities: null,
        kotikuntaKoodi: null,
      }
    }

    return {
      hetu: user.hetu,
      numberOfMarriages: user.numberOfMarriages,
      maritalStatus: user.maritalStatus,
      countryOfResidenceCode: user.countryOfResidenceCode,
      citizenshipCode: user.citizenshipCode,
      religiousCommunities: user.religiousCommunities,
      kotikuntaKoodi: user.kotikuntaKoodi,
    }
  }

  const {
    hetu,
    numberOfMarriages,
    maritalStatus,
    countryOfResidenceCode,
    citizenshipCode,
    religiousCommunities,
    kotikuntaKoodi,
  } = getHetuIdentifiedFields(user)

  return observable({
    hetu,
    firstNames: user?.firstNames
      ? editVtjArvo<Etunimet>(null, user.firstNames)
      : null,
    lastName: user?.lastName
      ? editVtjArvo<Sukunimi>(null, user.lastName)
      : null,
    authenticationSource: user?.authenticationSource ?? null,
    numberOfMarriages: numberOfMarriages ?? null,
    maritalStatus,
    countryOfResidenceCode,
    isAdult: user?.isAdult ?? null,
    citizenshipCode,
    religiousCommunities,
    kotikuntaKoodi,
    asiointikieli,
  })
}
